import { Add, ReportProblemOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { Context, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { IMainComponentType } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { ILocality } from 'src/generated-types'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { IDeviationFormWizardType } from 'src/generated-types/form-wizard'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  CreatePositionDeviationDialog,
  ICreateDeviationDialogProps,
} from '../../deviations/CreatePositionDeviationDialog'
import EquipmentDeviations from './EquipmentDeviations'
import EquipmentSectionLayout from './EquipmentSectionLayout'

interface Props {
  context: Context<any> | null | undefined
  customerId: EntityId
  localityId: EntityId | undefined
  mainComponentId: any
  mainComponent: IMainComponent | undefined
  locality: ILocality | undefined
  permission?: number
  storageId?: EntityId
  serviceStationId?: EntityId
  selectedLocalityId?: EntityId
  pop?: () => void
  enableDelete?: boolean
  isReadOnly?: boolean
  isLocalityPortal?: boolean
}

const DeviationSection = (props: Props) => {
  const { context, mainComponent, customerId, locality } = props
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['main-component', 'deviation'])
  const theme = useTheme()

  const onAddDeviationForComponent = useCallback(async () => {
    await showDialog(
      CreatePositionDeviationDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: locality?.id as EntityId,
          mooringId: mainComponent?.position?.mooringId as EntityId,
          position: mainComponent?.position,
          mainComponent: mainComponent,
          formWizard: IDeviationFormWizardType.Component,
          type:
            mainComponent?.type.componentType === IMainComponentType.Custom
              ? IDeviationType.Equipment
              : IDeviationType.MainComponent,
          disableSteps: true,
        } as ICreateDeviationDialogProps,
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }, [customerId, locality?.id, mainComponent])

  return (
    <EquipmentSectionLayout
      title={t('deviations', { ns: 'deviation' })}
      icon={
        <ReportProblemOutlined sx={{ fontSize: '18px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
      }
      buttonText={t('register-deviation', { ns: 'deviation' })}
      buttonIcon={<Add sx={{ fontSize: '14px' }} />}
      onButtonClick={onAddDeviationForComponent}
      context={context}
      permissions={[
        permissionValue.Locality.MainComponent.Deviation.Write,
        permissionValue.Locality.MainComponent.Document.Write,
        permissionValue.ClientAdminPermission,
        permissionValue.ServiceStation.Write,
        permissionValue.ClientAdminPortal.Equipment_Portal,
      ]}
      content={
        <EquipmentDeviations
          {...props}
          context={context || undefined}
          status={false}
          height={220}
          autoHeight={true}
          maximumVisibleRowCount={4}
          minimalEmptyStatus={true}
        />
      }
      showMoreContent={
        <EquipmentDeviations
          {...props}
          context={context || undefined}
          status={true}
          height={220}
          autoHeight={true}
          maximumVisibleRowCount={4}
          minimalEmptyStatus={true}
        />
      }
      contentSubtitle={t('open-deviations', { ns: 'deviation' })}
      expandText={t('show-closed-deviations', { ns: 'deviation' })}
      collapseText={t('hide-closed-deviations', { ns: 'deviation' })}
      isReadOnly={props.isReadOnly}
    />
  )
}

export default DeviationSection
